/**
 * Sanitize login returnTo to prevent open redirects.
 * Accepts only same-origin relative paths.
 */

const DEFAULT_RETURN = '/dashboard';

/**
 * @param {unknown} raw
 * @returns {string} safe internal pathname (+ optional search/hash)
 */
export function sanitizeReturnTo(raw) {
  if (raw == null) return DEFAULT_RETURN;
  let value = String(raw).trim();
  if (!value) return DEFAULT_RETURN;

  try {
    value = decodeURIComponent(value);
  } catch (e) {
    return DEFAULT_RETURN;
  }

  value = value.trim();
  if (!value) return DEFAULT_RETURN;

  // Absolute / protocol-relative / dangerous schemes
  const lower = value.toLowerCase();
  if (
    lower.startsWith('http:')
    || lower.startsWith('https:')
    || lower.startsWith('//')
    || lower.startsWith('javascript:')
    || lower.startsWith('data:')
    || lower.startsWith('vbscript:')
    || lower.startsWith('mailto:')
  ) {
    return DEFAULT_RETURN;
  }

  // Must be a path starting with a single slash (not //)
  if (!value.startsWith('/') || value.startsWith('//')) {
    return DEFAULT_RETURN;
  }

  // Reject backslash tricks and control chars
  if (value.includes('\\') || /[\u0000-\u001f\u007f]/.test(value)) {
    return DEFAULT_RETURN;
  }

  // Parse as relative URL against a fixed origin to validate structure
  try {
    const u = new URL(value, 'https://sa.invalid');
    if (u.origin !== 'https://sa.invalid') return DEFAULT_RETURN;
    if (!u.pathname.startsWith('/') || u.pathname.startsWith('//')) return DEFAULT_RETURN;
    const safe = `${u.pathname}${u.search}${u.hash}`;
    if (safe === '/login' || safe.startsWith('/login?')) return DEFAULT_RETURN;
    return safe || DEFAULT_RETURN;
  } catch (e) {
    return DEFAULT_RETURN;
  }
}

export function buildLoginPath(returnTo) {
  const safe = sanitizeReturnTo(returnTo);
  if (safe === DEFAULT_RETURN) return '/login';
  return `/login?returnTo=${encodeURIComponent(safe)}`;
}
